<template>
    <a
        :class="{ 'pointer-events-none text-opacity-50': disabled || isDownloading }"
        class="bg-primary-700 text-primary-800 bg-opacity-20 hover:bg-opacity-30 transition-colors rounded-lg py-2 px-3 inline-flex space-x-2"
        :target="linkTarget"
        :href="element.value"
        :aria-busy="isDownloading"
        @click="onLinkClicked"
    >
        <span>{{ element.label }}</span>
        <span class="flex">
            <Spinner v-if="isDownloading" class="h-4 w-4 my-auto" aria-hidden="true"></Spinner>
            <Download v-else-if="linkBehavior === 'download'" :class="{ 'opacity-50': disabled }" class="h-4 w-4 my-auto" aria-hidden="true"></Download>
            <Icon v-else :class="{ 'opacity-50': disabled }" class="-rotate-90 transform my-auto" :src="IconSource.DropdownPrimary"></Icon>
        </span>
    </a>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Download } from 'lucide-vue-next';
import { FormElement, LinkBehavior } from '@/types';
import Spinner from '@/components/ui/Spinner.vue';
import { shouldFetchDownloadTarget, useDownloadUrl } from '@/components/common/composables/useDownloadUrl';

export default defineComponent({
    components: {
        Download,
        Spinner,
    },
    props: {
        element: {
            type: Object as () => FormElement,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const { downloadUrl, isDownloading } = useDownloadUrl();

        return { downloadUrl, isDownloading };
    },
    computed: {
        linkBehavior(): LinkBehavior {
            return this.element.type_settings.link_behavior || 'new_tab';
        },
        linkTarget(): string {
            /* 'download' keeps _blank so a modified click opens the file instead of navigating the form away. */
            return this.linkBehavior === 'same_tab' ? '_self' : '_blank';
        },
    },
    methods: {
        onLinkClicked(event: MouseEvent) {
            if (this.disabled) {
                /* pointer-events-none already blocks the mouse, but not activating a focused link from the keyboard. */
                event.preventDefault();
                return;
            }

            if (this.linkBehavior !== 'download') {
                return;
            }

            /* Leave ctrl/cmd/shift/alt-clicks and non-primary buttons to the browser: opening the file in a new tab is a reasonable thing to ask for. */
            if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) {
                return;
            }

            const downloadTarget = {
                url: String(this.element.value ?? ''),
                route: this.element.type_settings.route,
                fileName: this.element.type_settings.download_filename,
            };

            if (!shouldFetchDownloadTarget(downloadTarget)) {
                return;
            }

            event.preventDefault();

            this.downloadUrl(downloadTarget);
        },
    },
});
</script>
